import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcModifyPharmacyListItemDto, FcPharmacyListItemDto, FormGroupValidationRules } from 'webcommon/shared';
import { PhoneNumberFormGroup } from '../../fc-item-component/index';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FormGroupExtended } from '../../generic/fc-list-validation/form-extended/index';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'ap-fc-pharmacy-list-modal',
  templateUrl: './fc-pharmacy-list-modal.component.html',
})
export class FcPharmacyListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-pharmacy-item';
  public selectedMasterPharmacyControlPks: string[] = [];
  public valueOfMasterPharmacyChanged = false;

  // List Item with default value
  public listItem: FcPharmacyListItemDto = {
    Id: '',
    Name: '',
    MasterPharmacy: {
      MasterPharmacyName: '',
      MasterPharmacyUid: '',
      NPI: '',
      NCPDPID: '',
    },
    Address: {
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Zip: '',
      ZipUid: '',
      CountyName: '',
      CountyCode: '',
    },
    Phone1: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Phone2: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Phone3: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Inactive: false,
  };

  public listItemDefaultValidation: FormGroupValidationRules = {
    Name: {
      IsRequired: true,
      MaxLength: 256,
    },
    MasterPharmacyName: {
      MaxLength: 256,
      IsRequired: false,
    },
    MasterPharmacyUid: {
      IsRequired: false,
    },
    Contact: {
      MaxLength: 50,
      IsRequired: false,
    },
  };

  // Form Controls
  public pharmacyNameControl: FormControl = new FormControl('');
  public masterPharmacyControl: FormControl = new FormControl('');
  public masterPharmacyUidControl: FormControl = new FormControl('');
  public npiControl: FormControl = new FormControl('');
  public ncpdpidControl: FormControl = new FormControl('');
  public inactiveControl: FormControl = new FormControl(false);

  // Form Group
  public addressGroup = new FormGroupExtended({});
  public phoneOneGroup = new FormGroup<PhoneNumberFormGroup>({});
  public phoneTwoGroup = new FormGroup<PhoneNumberFormGroup>({});
  public phoneThreeGroup = new FormGroup<PhoneNumberFormGroup>({});

  public form = new FormGroup({
    name: this.pharmacyNameControl,
    masterPharmacy: this.masterPharmacyControl,
    masterPharmacyUid: this.masterPharmacyUidControl,
    npi: this.npiControl,
    ncpdpid: this.ncpdpidControl,
    inactive: this.inactiveControl,
    address: this.addressGroup,
    phoneOne: this.phoneOneGroup,
    phoneTwo: this.phoneTwoGroup,
    phoneThree: this.phoneThreeGroup,
  });

  constructor(injector: Injector) {
    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();

    // Load List Item and set default validation rules
    this.loadListItem(this.listItemDefaultValidation);
  }

  public setForm(): void {
    // Pharmacy Name Control
    this.pharmacyNameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.pharmacyNameControl.setValue(this.listItem.Name);

    // Master Pharmacy Control
    this.masterPharmacyControl.setValidators(this.fcListValidation.getValidators('MasterPharmacy'));
    this.masterPharmacyControl.setValue(this.listItem.MasterPharmacy?.MasterPharmacyName);

    // NPI Control
    this.npiControl.disable();
    this.npiControl.setValidators(this.fcListValidation.getValidators('NPI'));
    this.npiControl.setValue(this.listItem.MasterPharmacy?.NPI);

    // NCPDPID Control
    this.ncpdpidControl.disable();
    this.ncpdpidControl.setValidators(this.fcListValidation.getValidators('NCPDPID'));
    this.ncpdpidControl.setValue(this.listItem.MasterPharmacy?.NCPDPID);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    this.validateForm();
    this.populateMasterPharmacyFindControl();
  }

  public onSetAreaCode(areaCode: string): void {
    const phoneNumberControl = this.phoneOneGroup.get('phoneNumber');
    if (phoneNumberControl != null && !phoneNumberControl.value) {
      phoneNumberControl.patchValue(areaCode);
      phoneNumberControl.markAsDirty();
    }
  }

  public onMasterPharmacyValueChanged(event: boolean): void {
    this.valueOfMasterPharmacyChanged = event;
  }

  public getFindControlValidation(controlName: string): any {
    if (this.fcListValidation.getValidators(controlName).length > 0) {
      return this.fcListValidation.getValidators(controlName);
    } else {
      return this.listItemDefaultValidation[controlName]?.IsRequired;
    }
  }

  public populateMasterPharmacyFindControl(): void {
    if (this.listItem.MasterPharmacy?.MasterPharmacyUid != null && this.listItem.MasterPharmacy.MasterPharmacyUid.length > 0) {
      this.selectedMasterPharmacyControlPks = [this.listItem.MasterPharmacy.MasterPharmacyUid];
    } else {
      this.selectedMasterPharmacyControlPks = [];
    }
  }

  public onMasterPharmacyDataPopulate(masterPharmacyArray: FcPharmacyListItemDto[]): void {
    const masterPharmacyData: FcPharmacyListItemDto = { ...masterPharmacyArray[0] };
    if (masterPharmacyData.Name) {
      this.masterPharmacyControl.setValue(masterPharmacyData.Name);
      this.masterPharmacyUidControl.setValue(masterPharmacyData.MasterPharmacyUid);
      if (masterPharmacyData.NPI) {
        this.npiControl.setValue(masterPharmacyData.NPI);
      }
      if (masterPharmacyData.NCPDPID) {
        this.ncpdpidControl.setValue(masterPharmacyData.NCPDPID);
      }
    } else {
      this.masterPharmacyControl.setValue('');
      this.masterPharmacyUidControl.setValue('');
      this.ncpdpidControl.setValue('');
      this.npiControl.setValue('');
    }
    if (masterPharmacyData.Name && this.valueOfMasterPharmacyChanged) {
      this.pharmacyNameControl.setValue(masterPharmacyData.Name);
      if (masterPharmacyData.Address) {
        this.listItem.Address = {
          Address1: masterPharmacyData.Address.Address1,
          Address2: masterPharmacyData.Address.Address2,
          City: masterPharmacyData.Address.City,
          State: masterPharmacyData.Address.State,
          Zip: masterPharmacyData.Address.Zip,
          ZipUid: masterPharmacyData.Address.ZipUid,
        };
      }
      if (masterPharmacyData.Phone1) {
        this.listItem.Phone1 = {
          PhoneNumber: masterPharmacyData.Phone1.PhoneNumber,
          PhoneType: masterPharmacyData.Phone1.PhoneType,
          PhoneTypeUid: masterPharmacyData.Phone1.PhoneTypeUid,
        };
      }
      if (masterPharmacyData.Phone2) {
        this.listItem.Phone2 = {
          PhoneNumber: masterPharmacyData.Phone2.PhoneNumber,
          PhoneType: masterPharmacyData.Phone2.PhoneType,
          PhoneTypeUid: masterPharmacyData.Phone2.PhoneTypeUid,
        };
      }
      if (masterPharmacyData.Phone3) {
        this.listItem.Phone3 = {
          PhoneNumber: masterPharmacyData.Phone3.PhoneNumber,
          PhoneType: masterPharmacyData.Phone3.PhoneType,
          PhoneTypeUid: masterPharmacyData.Phone3.PhoneTypeUid,
        };
      }
    }
  }

  public override save(): void {
    const values = this.form.getRawValue();
    this.isSubmitted = true;

    const modifyItemDto: FcModifyPharmacyListItemDto = {
      Name: values.name,
      Address: {
        Address1: values.address.addressOne,
        Address2: values.address.addressTwo,
        City: values.address.city,
        State: values.address.state,
        Zip: values.address.zip,
        Country: values.address.country,
        CountyName: values.address.countyName,
        CountyCode: values.address.countyCode,
      },
      Inactive: values.inactive,
    };

    if (values.masterPharmacy && values.masterPharmacyUid) {
      modifyItemDto.MasterPharmacy = {
        MasterPharmacyName: values.masterPharmacy,
        MasterPharmacyUid: values.masterPharmacyUid,
        NPI: values.npi,
        NCPDPID: values.ncpdpid,
      };
    }

    if (values.phoneOne.phoneNumber && values.phoneOne.phoneType && values.phoneOne.phoneTypeUid) {
      modifyItemDto.Phone1 = {
        PhoneNumber: values.phoneOne.phoneNumber,
        PhoneType: values.phoneOne.phoneType,
        PhoneTypeUid: values.phoneOne.phoneTypeUid,
      };
    }
    if (values.phoneTwo.phoneNumber && values.phoneTwo.phoneType && values.phoneTwo.phoneTypeUid) {
      modifyItemDto.Phone2 = {
        PhoneNumber: values.phoneTwo.phoneNumber,
        PhoneType: values.phoneTwo.phoneType,
        PhoneTypeUid: values.phoneTwo.phoneTypeUid,
      };
    }
    if (values.phoneThree.phoneNumber && values.phoneThree.phoneType && values.phoneThree.phoneTypeUid) {
      modifyItemDto.Phone3 = {
        PhoneNumber: values.phoneThree.phoneNumber,
        PhoneType: values.phoneThree.phoneType,
        PhoneTypeUid: values.phoneThree.phoneTypeUid,
      };
    }

    super.save<FcModifyPharmacyListItemDto>(modifyItemDto);
  }
}
